/**
 * Remembered calendar view: the user's last chosen switcher view (year / month / week /
 * work week / day) survives reloads. A dedicated small key rather than a devicePrefs
 * field: the value is calendar-surface state, not device state, and the five known ids
 * are validated on load - anything else degrades to the default month view.
 */

import type { ViewId } from './CalendarPage';

export const CALENDAR_VIEW_STORAGE_KEY = 'vc.calendarView';

/** The minimal storage surface the view preference needs (structural - the SSO
 * KeyValueStorage and the DOM Storage both satisfy it). */
export interface ViewPrefStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const KNOWN_VIEWS: readonly ViewId[] = [
  'multiMonthYear',
  'dayGridMonth',
  'timeGridWeek',
  'workWeek',
  'timeGridDay',
];

/** Pure load: unknown ids, non-strings and missing keys fall back to the month view. */
export function loadCalendarView(storage: ViewPrefStorage): ViewId {
  try {
    const raw = storage.getItem(CALENDAR_VIEW_STORAGE_KEY);
    return KNOWN_VIEWS.includes(raw as ViewId) ? (raw as ViewId) : 'dayGridMonth';
  } catch {
    return 'dayGridMonth';
  }
}

/** Pure save; storage failures (private mode quota) are swallowed - the view is cosmetic. */
export function saveCalendarView(view: ViewId, storage: ViewPrefStorage): void {
  try {
    storage.setItem(CALENDAR_VIEW_STORAGE_KEY, view);
  } catch {
    // cosmetic preference: ignore write failures
  }
}
